import { useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '@/services/api';

interface State<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useFetch<T = unknown>(path: string | null) {
  const [state, setState] = useState<State<T>>({ data: null, loading: !!path, error: null });

  const run = useCallback(
    async (signal?: AbortSignal) => {
      if (!path) {
        setState({ data: null, loading: false, error: null });
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const data = await api.get<T>(path, signal);
        setState({ data, loading: false, error: null });
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        const msg = e instanceof ApiError ? e.message : (e as Error).message;
        setState({ data: null, loading: false, error: msg });
      }
    },
    [path],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    run(ctrl.signal);
    return () => ctrl.abort();
  }, [run]);

  return { ...state, refresh: () => run() };
}
